import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import {
  CheckIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';
import { cmsImportsApiClient } from '@/services/api/cmsImportsService';
import type {
  CmsImportedTaskFilterSchema,
  CmsImportFilterOptionsSchema,
} from '@/services/api/generated/cmsImportsService';

interface ImportScopeSectionProps {
  importId: string;
  filterOptions: CmsImportFilterOptionsSchema | undefined;
  savedFilters: CmsImportedTaskFilterSchema | null;
  taskCountInScope: number;
  onScopeChange: (filters: CmsImportedTaskFilterSchema) => void;
  onHasUnsavedChangesChange: (hasChanges: boolean) => void;
}

interface FilterOption {
  label: string;
  value: string;
}

export const ImportScopeSection: React.FC<ImportScopeSectionProps> = ({
  importId,
  filterOptions,
  savedFilters,
  taskCountInScope,
  onScopeChange,
  onHasUnsavedChangesChange,
}) => {
  // Local filter state (can be edited before saving)
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [caseIds, setCaseIds] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Mutation for saving filters
  const [updateFilters, { isLoading: isSaving }] =
    cmsImportsApiClient.useCmsImportUpdateFiltersMutation();

  // Initialize from saved filters
  useEffect(() => {
    if (savedFilters) {
      setAssigneeIds(savedFilters.assignee_internal_ids ?? []);
      setCaseIds(savedFilters.case_internal_ids ?? []);
      setStatuses(savedFilters.statuses ?? []);
    }
  }, [savedFilters]);

  // Track unsaved changes
  useEffect(() => {
    const savedAssignees = savedFilters?.assignee_internal_ids ?? [];
    const savedCases = savedFilters?.case_internal_ids ?? [];
    const savedStatuses = savedFilters?.statuses ?? [];

    // Spread arrays before sorting to avoid mutating state
    const assigneesChanged =
      JSON.stringify([...assigneeIds].sort()) !==
      JSON.stringify([...savedAssignees].sort());
    const casesChanged =
      JSON.stringify([...caseIds].sort()) !==
      JSON.stringify([...savedCases].sort());
    const statusesChanged =
      JSON.stringify([...statuses].sort()) !==
      JSON.stringify([...savedStatuses].sort());

    const hasChanges = assigneesChanged || casesChanged || statusesChanged;
    setHasUnsavedChanges(hasChanges);
    onHasUnsavedChangesChange(hasChanges);
  }, [assigneeIds, caseIds, statuses, savedFilters, onHasUnsavedChangesChange]);

  // Notify parent when scope changes (for task query)
  useEffect(() => {
    onScopeChange({
      assignee_internal_ids: assigneeIds.length > 0 ? assigneeIds : null,
      case_internal_ids: caseIds.length > 0 ? caseIds : null,
      statuses: statuses.length > 0 ? statuses : null,
    });
  }, [assigneeIds, caseIds, statuses, onScopeChange]);

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      await updateFilters({
        id: importId,
        updateFiltersRequestSchema: {
          filters: {
            assignee_internal_ids: assigneeIds.length > 0 ? assigneeIds : null,
            case_internal_ids: caseIds.length > 0 ? caseIds : null,
            statuses: statuses.length > 0 ? statuses : null,
          },
        },
      }).unwrap();

      toast.success('Import scope saved');
    } catch (error: unknown) {
      const err = error as { data?: { detail?: string } };
      toast.error(err?.data?.detail || 'Failed to save import scope');
    }
  }, [importId, updateFilters, assigneeIds, caseIds, statuses]);

  // Filter options
  const assigneeOptions: FilterOption[] = useMemo(
    () =>
      filterOptions?.assignees.map((a) => ({
        label: a.name,
        value: a.internal_id,
      })) ?? [],
    [filterOptions],
  );

  const caseOptions: FilterOption[] = useMemo(
    () =>
      filterOptions?.cases.map((c) => ({
        label: c.name,
        value: c.internal_id,
      })) ?? [],
    [filterOptions],
  );

  const statusOptions: FilterOption[] = useMemo(
    () =>
      filterOptions?.statuses.map((s) => ({
        label: s,
        value: s,
      })) ?? [],
    [filterOptions],
  );

  return (
    <div className="flex-shrink-0 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
            Import Scope
          </span>
          {hasUnsavedChanges && (
            <Badge color="amber" className="text-xs">
              Unsaved changes
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-blue-700 dark:text-blue-300">
            <span className="font-semibold">{taskCountInScope}</span> tasks in
            scope
          </span>
          <Button
            color={hasUnsavedChanges ? 'blue' : 'white'}
            onClick={handleSave}
            disabled={isSaving || !hasUnsavedChanges}
            className="text-sm"
          >
            {isSaving ? (
              'Saving...'
            ) : hasUnsavedChanges ? (
              'Save Scope'
            ) : (
              <>
                <CheckIcon className="h-4 w-4 mr-1" />
                Saved
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <ScopeFilterDropdown
          label="Assignees"
          options={assigneeOptions}
          selectedValues={assigneeIds}
          onSelectionChange={setAssigneeIds}
        />
        <ScopeFilterDropdown
          label="Cases"
          options={caseOptions}
          selectedValues={caseIds}
          onSelectionChange={setCaseIds}
        />
        <ScopeFilterDropdown
          label="Statuses"
          options={statusOptions}
          selectedValues={statuses}
          onSelectionChange={setStatuses}
        />
      </div>

      {hasUnsavedChanges && (
        <div className="mt-3 flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs">
          <ExclamationCircleIcon className="h-4 w-4" />
          <span>
            Changes to import scope are not saved yet. Save before importing to
            apply them.
          </span>
        </div>
      )}
    </div>
  );
};

// Scope Filter Dropdown Component with Floating UI
interface ScopeFilterDropdownProps {
  label: string;
  options: FilterOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
}

const ScopeFilterDropdown: React.FC<ScopeFilterDropdownProps> = ({
  label,
  options,
  selectedValues,
  onSelectionChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const isFiltered = selectedValues.length > 0;
  const allSelected = selectedValues.length === 0;

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  // Get display label for button
  const getDisplayLabel = () => {
    if (allSelected) {
      return `All (${options.length})`;
    }
    if (selectedValues.length === 1) {
      const selected = options.find((o) => o.value === selectedValues[0]);
      return selected?.label || '1 selected';
    }
    return `${selectedValues.length} selected`;
  };

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter((v) => v !== value));
    } else {
      onSelectionChange([...selectedValues, value]);
    }
  };

  const handleSelectAll = () => {
    onSelectionChange([]);
  };

  const handleClearAll = () => {
    // When clearing, we select nothing specific (which means "all" in our logic)
    // But if we want to truly clear to show nothing, we'd need different logic
    // For spreadsheet-like behavior, "Clear" should deselect all specific selections
    onSelectionChange([]);
  };

  const handleSelectAllItems = () => {
    // Select all visible (filtered) items, merging with existing selections
    const filteredValues = filteredOptions.map((o) => o.value);
    const newSelection = new Set([...selectedValues, ...filteredValues]);
    onSelectionChange(Array.from(newSelection));
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
        {label}
      </span>

      {/* Trigger Button */}
      <button
        type="button"
        ref={refs.setReference}
        {...getReferenceProps()}
        className={cn(
          'flex items-center justify-between gap-2 px-3 py-2 min-w-[180px] rounded-lg border text-sm',
          'bg-white dark:bg-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-700',
          'transition-colors duration-150',
          isFiltered
            ? 'border-blue-400 dark:border-blue-600'
            : 'border-zinc-300 dark:border-zinc-600',
        )}
      >
        <span className="truncate">{getDisplayLabel()}</span>
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 text-zinc-500 transition-transform duration-150',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {/* Floating Dropdown */}
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className="z-50 bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 shadow-lg w-[280px]"
        >
          {/* Search Input */}
          <div className="p-2 border-b border-zinc-200 dark:border-zinc-700">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 p-2 border-b border-zinc-200 dark:border-zinc-700">
            <button
              type="button"
              onClick={handleClearAll}
              className="flex-1 px-2 py-1 text-xs font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded transition-colors"
            >
              Clear
            </button>
            <div className="w-px h-4 bg-zinc-300 dark:bg-zinc-600" />
            <button
              type="button"
              onClick={handleSelectAllItems}
              className="flex-1 px-2 py-1 text-xs font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded transition-colors"
            >
              Select All
            </button>
          </div>

          {/* Options List */}
          <div className="max-h-[240px] overflow-auto p-1">
            {/* "All" option (no filter) */}
            <button
              type="button"
              onClick={handleSelectAll}
              className={cn(
                'w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left',
                'hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors',
                allSelected && 'bg-blue-50 dark:bg-blue-900/30',
              )}
            >
              <div
                className={cn(
                  'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                  allSelected
                    ? 'bg-blue-600 border-blue-600'
                    : 'border-zinc-300 dark:border-zinc-600',
                )}
              >
                {allSelected && <CheckIcon className="h-3 w-3 text-white" />}
              </div>
              <span className="font-medium">All ({options.length})</span>
            </button>

            {/* Divider */}
            <div className="h-px bg-zinc-200 dark:bg-zinc-700 my-1" />

            {/* Individual Options */}
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-3 text-sm text-zinc-500 text-center">
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <button
                    type="button"
                    key={option.value}
                    onClick={() => handleToggle(option.value)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left',
                      'hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors',
                      isSelected && 'bg-blue-50 dark:bg-blue-900/30',
                    )}
                  >
                    <div
                      className={cn(
                        'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                        isSelected
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-zinc-300 dark:border-zinc-600',
                      )}
                    >
                      {isSelected && (
                        <CheckIcon className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <span className="truncate">{option.label}</span>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer showing selection count */}
          {isFiltered && (
            <div className="px-2 py-1.5 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400">
              {selectedValues.length} of {options.length} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
};
